import React, { useEffect, useState } from 'react';
import { RefreshCw, RotateCcw, Info, Send } from 'lucide-react';
import { ActionMenu, Button, DataTable, Modal } from '../../../shared/components';
import { useNotificationsManagement } from '../../../shared/hooks';
import NotificationsStats from './NotificationsStats';
import NotificationsFilters from './NotificationsFilters';
import MarketingBlockedResendModal from './MarketingBlockedResendModal';
import PhoneCorrectionModal from './PhoneCorrectionModal';
import MessagePreview from './MessagePreview';
import { notificationsService } from '../../../shared/services/notificationsService';
import { getQueueStatus, getDeliveryStatus, getNotificationTypeLabel } from './notificationTypes';

const formatDateTime = (value) => {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }),
    time: d.toLocaleTimeString('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      hour12: false
    })
  };
};

const Badge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.badge}`}>
    {status.label}
  </span>
);

// Errores de Meta por número inalcanzable que se resuelven corrigiendo el teléfono
const UNDELIVERABLE_ERROR_CODES = [131026];

// Estados de una fila ya resuelta: no admiten corregir el teléfono
const RESOLVED_STATUSES = ['cancelled', 'resent'];

const canCorrectPhone = (n) =>
  n.deliveryStatus === 'failed' &&
  !RESOLVED_STATUSES.includes(n.status) &&
  UNDELIVERABLE_ERROR_CODES.includes(n.providerErrorCode);

const NotificationsList = () => {
  const {
    notifications,
    stats,
    loading,
    statsLoading,
    retrying,
    error,
    actionMessage,
    filters,
    pagination,
    fetchNotifications,
    refresh,
    applyFilters,
    applyFiltersNow,
    changeYear,
    changePage,
    clearFilters,
    retryFailed,
    completeAction
  } = useNotificationsManagement();

  const [selected, setSelected] = useState(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [phoneCorrection, setPhoneCorrection] = useState(null);
  const [viewError, setViewError] = useState(null);

  // Abre el mensaje que reemplazó a uno reenviado, con su estado actual
  const viewReplacement = async (n) => {
    setViewError(null);
    try {
      const replacement = await notificationsService.getNotification(n.resentAsId);
      setSelected({ ...replacement, isReplacement: true });
    } catch (err) {
      setViewError(err.message);
    }
  };

  // Carga inicial única
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      key: 'createdAt',
      label: 'Fecha y hora',
      render: (n) => {
        const { date, time } = formatDateTime(n.createdAt);
        return (
          <div className="whitespace-nowrap">
            <div className="text-sm font-medium text-gray-900">{date}</div>
            <div className="text-xs font-mono tabular-nums text-gray-500">{time}</div>
          </div>
        );
      }
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (n) => <div className="text-sm text-gray-900">{getNotificationTypeLabel(n.type)}</div>
    },
    {
      key: 'recipient',
      label: 'Destinatario',
      render: (n) => (
        <div>
          <div className="text-sm text-gray-900">{n.student?.name || '—'}</div>
          <div className="text-xs font-mono text-gray-500">{n.recipientPhone}</div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Estado en la cola',
      render: (n) => (
        <div>
          <Badge status={getQueueStatus(n.status)} />
          {n.retryCount > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Reintentos: {n.retryCount}/{n.maxRetries}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'deliveryStatus',
      label: 'Estado de entrega',
      render: (n) => <Badge status={getDeliveryStatus(n.deliveryStatus)} />
    },
    {
      key: 'deliveryUpdatedAt',
      label: 'Última actualización',
      render: (n) => {
        if (!n.deliveryUpdatedAt) return <span className="text-gray-400">—</span>;
        const { date, time } = formatDateTime(n.deliveryUpdatedAt);
        return (
          <div className="whitespace-nowrap">
            <div className="text-sm text-gray-900">{date}</div>
            <div className="text-xs font-mono tabular-nums text-gray-500">{time}</div>
          </div>
        );
      }
    },
    {
      key: 'error',
      label: 'Error',
      render: (n) =>
        n.providerErrorCode || n.errorMessage ? (
          <div className="max-w-xs">
            {n.providerErrorCode && (
              <div className="text-xs font-mono font-semibold text-red-700">
                Código {n.providerErrorCode}
              </div>
            )}
            {n.errorMessage && (
              <div className="text-xs text-red-600 truncate" title={n.errorMessage}>
                {n.errorMessage}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )
    },
    {
      key: 'actions',
      label: '',
      render: (n) => (
        <ActionMenu
          items={[
            { key: 'view', label: 'Ver mensaje', onClick: () => setSelected(n) },
            {
              key: 'replacement',
              label: 'Ver reenvío',
              hidden: !n.resentAsId,
              onClick: () => viewReplacement(n)
            },
            {
              key: 'phone',
              label: 'Corregir teléfono',
              hidden: !canCorrectPhone(n),
              onClick: () => setPhoneCorrection(n)
            }
          ]}
        />
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mensajes de WhatsApp</h1>
          <p className="text-gray-600 mt-1">
            Seguimiento del envío y la entrega de cada mensaje, del más reciente al más antiguo
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading || statsLoading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={() => setResendOpen(true)}
            title="Reenvía por la plantilla informativa los avisos que Meta bloqueó por ser marketing"
          >
            <Send className="w-4 h-4 mr-2" />
            Reenviar bloqueados por Meta
          </Button>
          <Button
            onClick={retryFailed}
            disabled={retrying}
            title="Reintenta todos los mensajes fallidos, sin importar los filtros aplicados"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {retrying ? 'Reintentando...' : 'Reintentar fallidos'}
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          Enviado = Meta lo aceptó; Entregado/Leído = confirmado por WhatsApp.
        </span>
      </div>

      {actionMessage && (
        <div
          className={`text-sm rounded-lg px-4 py-3 border ${
            actionMessage.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {viewError && (
        <div className="text-sm rounded-lg px-4 py-3 border bg-red-50 border-red-200 text-red-800">
          {viewError}
        </div>
      )}

      <NotificationsStats stats={stats} loading={statsLoading} />

      <NotificationsFilters
        filters={filters}
        onFiltersChange={applyFilters}
        onClearFilters={clearFilters}
        onSearch={applyFiltersNow}
        onYearChange={changeYear}
        availableYears={stats?.availableYears || []}
        loading={loading}
      />

      <div className="bg-white rounded-lg shadow">
        <DataTable
          section="Mensajes"
          data={notifications}
          columns={columns}
          loading={loading}
          error={error}
          onRetry={() => fetchNotifications()}
          emptyMessage="No se encontraron mensajes para los filtros seleccionados"
          showSearch={false}
          showFilters={false}
          pagination={pagination}
          onPageChange={changePage}
        />
      </div>

      <MarketingBlockedResendModal
        isOpen={resendOpen}
        onClose={() => setResendOpen(false)}
        onDone={completeAction}
      />

      <PhoneCorrectionModal
        notification={phoneCorrection}
        onClose={() => setPhoneCorrection(null)}
        onDone={completeAction}
      />

      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.isReplacement ? 'Mensaje de reemplazo' : 'Detalle del mensaje'}
        size="lg"
      >
        {selected && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              {getNotificationTypeLabel(selected.type)} · {selected.student?.name || '—'} ·{' '}
              <span className="font-mono">{selected.recipientPhone}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge status={getQueueStatus(selected.status)} />
              <Badge status={getDeliveryStatus(selected.deliveryStatus)} />
            </div>
            <MessagePreview message={selected.message} />
            {(selected.providerErrorCode || selected.errorMessage) && (
              <div className="text-sm text-red-700">
                {selected.providerErrorCode && <div>Código de error: {selected.providerErrorCode}</div>}
                {selected.errorMessage && <div>{selected.errorMessage}</div>}
              </div>
            )}
            {selected.providerMessageId && (
              <div className="text-xs text-gray-500 font-mono break-all">
                ID en Meta: {selected.providerMessageId}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NotificationsList;
